import { convertQueryCommandToQueryString, Equal, INestedWhereCommand, Like } from '@llws/dynamic-router-utils';
import { ICityRegions, IGeoCities } from '@llws/lift-entity-interfaces';
import vm from '@/main';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { IMetaResponse } from '../interfaces';

/**
 * Fetches Cities based on City and Province search params <br>
 * API Endpoint: ```
 * `/property-api/cities?relations=province:p`
 *   + `&limit=${limit}`
 *   + `${(cityValue ? `&where=cityName~like:${cityValue}` : '')}`
 *   + `${(provinceValue && cityValue ? `,(p.provinceName~like:${provinceValue},or~p.provinceCode~like:${provinceValue})` : ''  )}`
 * ``` <br> via {@link adminRequest.get}
 * @param {string} cityValue - city name search value
 * @param {string} provinceValue - province name search value
 * @param {number} limit - limit of the return values
 */
export const get = async (
  cityValue: string = '',
  provinceValue: string = '',
  limit: number = 25,
) => {
  const query = convertQueryCommandToQueryString({
    relations: [
      {
        path: 'province',
        alias: 'p',
      },
      {
        path: 'p.country',
        alias: 'c',
      },
    ],
    where: [
      ...(cityValue ? [
        Like('cityName', cityValue),
      ] : []),
      ...(cityValue && provinceValue) ? [{
        condition: 'and',
        whereCommands: [
          {
            operator: 'like',
            notOperator: false,
            field: 'p.provinceName',
            value: provinceValue,
            condition: 'or',
          },
          {
            operator: 'like',
            notOperator: false,
            field: 'p.provinceCode',
            value: provinceValue,
            condition: 'or',
          },
        ],
      }] as INestedWhereCommand[] : [],
    ],
    limit,
  });
  return adminRequest.get(`/property-api/cities${query}`);
};

/**
 * Precisely Fetches City based on City and Province search params <br>
 * API Endpoint: ```
 * `/property-api/cities?relations=province:p`
 *   + `&limit=${limit}`
 *   + `${(cityValue ? `&where=cityName:${cityValue}` : '')}`
 *   + `${(provinceValue && cityValue ? `,(p.provinceName:${provinceValue},or~p.provinceCode:${provinceValue})` : ''  )}`
 * ``` <br> via {@link adminRequest.get}
 * @param {string} cityValue - city name search value
 * @param {string} provinceValue - province name search value
 * @param {number} limit - limit of the return values
 */
export const getCityPrecisely = async (
  cityValue: string = '',
  provinceValue: string = '',
) => {
  const query = convertQueryCommandToQueryString({
    relations: [
      {
        path: 'province',
        alias: 'p',
      },
    ],
    where: [
      ...(cityValue ? [
        Equal('cityName', cityValue),
      ] : []),
      ...(cityValue && provinceValue) ? [{
        condition: 'and',
        whereCommands: [
          {
            operator: '',
            notOperator: false,
            field: 'p.provinceName',
            value: provinceValue,
            condition: 'or',
          },
          {
            operator: '',
            notOperator: false,
            field: 'p.provinceCode',
            value: provinceValue,
            condition: 'or',
          },
        ],
      }] as INestedWhereCommand[] : [],
    ],
    limit: 1,
  });
  return adminRequest.get<IGeoCities[]>(`/property-api/cities${query}`);
};

/**
 * Fetches Regions by City and Client <br>
 * API Endpoint - `/property-api/regions?relations=cityoffered:co&where=co.clientId:${clientId},co.cityId:${cityId}&orderBy=regionName~asc` <br> via {@link adminRequest.get}
 * @param {number} clientId - id of the client
 * @param {number} cityId - id of the city
 */
export const getRegionsByCityAndClient = async (clientId: number, cityId: number) => adminRequest.get(
  `/property-api/regions?relations=cityoffered:co`
  + `&where=co.clientId:${clientId},co.cityId:${cityId}`
  + `&orderBy=regionName~asc`,
);
/**
 * Fetches Regions by City Offered <br>
 * API Endpoint - `/property-api/regions?&where=cityofferedId:${cityOfferedId}&orderBy=regionName~asc` <br> via {@link adminRequest.get}
 * @param {number} cityOfferedId - id of the city offered
 */
export const getRegionsByCityOfferedAndClient = async (cityOfferedId: number) => adminRequest.get<ICityRegions[], IMetaResponse>(
  // eslint-disable-next-line prefer-template
  `/property-api/regions?`
  + `&where=cityofferedId:${cityOfferedId}`
  + `&orderBy=regionName~asc`
  + (vm && vm()?.$route.params.websiteId ? `&siteId=${vm()?.$route.params.websiteId}` : ''),
);

/**
 * Fetches City Properties <br>
 * API Endpoint - `/property-api/cities?view=client-properties-sumary` <br> via {@link adminRequest.get}
 */
export const getCityProperties = async () => adminRequest.get('/property-api/cities?view=client-properties-sumary');

export default {
  get,
  getRegionsByCityAndClient,
  getRegionsByCityOfferedAndClient,
  getCityProperties,
};
